import React, { useState, useEffect } from 'react';
import { Download, FileText, Image as ImageIcon, Music, ExternalLink, ChevronRight, Info, CheckCircle2 } from 'lucide-react';
import '../../../css/EmrManagement.css';
import api from '../../../api/axiosConfig';

interface EmrRecord {
  id: string; 
  emrId: string;
  parentName: string;
  childName: string;
  date: string;
  center: string;
  childDob?: string;
  childGender?: string;
  photoKey?: string;
}

interface Report {
  _id: string;
  id: string;
  reportType: string;
  category: string;
  key: string;
  viewUrl: string;
}

const EmrManagement: React.FC = () => {
  // Search Fields
  const [emrIdFilter, setEmrIdFilter] = useState('');
  const [parentNameFilter, setParentNameFilter] = useState('');
  const [childNameFilter, setChildNameFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [centerFilter, setCenterFilter] = useState('');

  // Dropdown data
  const [centers, setCenters] = useState<any[]>([]);

  // State data
  const [records, setRecords] = useState<EmrRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selected Record details
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState<EmrRecord | null>(null);
  const [s3Files, setS3Files] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // Questionnaire answers state
  const [answersData, setAnswersData] = useState<any[]>([]);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [answersError, setAnswersError] = useState<string | null>(null);
  const [openSubsections, setOpenSubsections] = useState<Record<string, boolean>>({});

  const tabs = ['Reports', 'Audio', 'Images', 'History', 'Questionnaire', 'Dairy'];

  // Fetch Centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await api.center_api.get('/api/v1/centers/public');
        if (response.data && response.data.success) {
          setCenters(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching centers:', err);
      }
    };
    fetchCenters();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setRecords([]);
    setSelectedRecordId(null);
    setSelectedRecordDetails(null);

    const isBasicSearch = emrIdFilter || parentNameFilter || childNameFilter;
    const isApptSearch = dateFilter && centerFilter;

    if (!isBasicSearch && !isApptSearch) {
      setError("Please enter search criteria.");
      setLoading(false);
      return;
    }

    try {
      let basicResults: any[] = [];
      let apptResults: any[] = [];

      // Scenario A: Parent/Child/EMR search
      if (isBasicSearch) {
        const params: any = {};
        if (emrIdFilter) params.emrId = emrIdFilter;
        if (parentNameFilter) params.parentName = parentNameFilter;
        if (childNameFilter) params.childName = childNameFilter;

        const response = await api.parentchildApi.get('/children/all', { params });
        if (response.data && response.data.data) {
          basicResults = response.data.data;
        }
      }

      // Scenario B: Appointment search
      if (isApptSearch) {
        const response = await api.appointment_api.get(
          `/api/appointments/center/${centerFilter}/date?date=${dateFilter}&include_cancelled=true`
        );
        
        let extractedBookings: any[] = [];
        if (response.data && response.data.slots) {
          response.data.slots.forEach((slot: any) => {
            if (slot.bookings && slot.bookings.length > 0) {
              slot.bookings.forEach((booking: any) => {
                if (booking.child_id || booking.emrId) {
                  extractedBookings.push(booking);
                }
              });
            }
          });
        }

        const detailPromises = extractedBookings.map(async (b: any) => {
          const targetEmrId = b.emrId || b.child_id;
          try {
            const detailRes = await api.parentchildApi.get(`/children/emr/${targetEmrId}/details`);
            const detailPayload = detailRes?.data?.data ?? detailRes?.data ?? null;
            const matched = Array.isArray(detailPayload) ? detailPayload[0] : detailPayload;

            if (matched) {
              const isEmrMatch = (matched.emrId || matched.childId) === targetEmrId;
              const isNameMatch = matched.childName && b.child_name && matched.childName.toLowerCase() === b.child_name.toLowerCase();
              if (isEmrMatch || isNameMatch) {
                return {
                  ...matched,
                  parentName: matched.parentName || b.parent_name || b.parentName || '-',
                  childName: matched.childName || b.child_name || b.childName || '-',
                  apptDate: dateFilter,
                  apptCenter: centerFilter
                };
              }
            }
          } catch (e) {
            console.error(`Error fetching details for EMR ${targetEmrId}:`, e);
          }

          // Fallback to real booking information from appointment API response
          return {
            _id: b.booking_id || targetEmrId,
            emrId: targetEmrId,
            parentName: b.parent_name || b.parentName || '-',
            childName: b.child_name || b.childName || '-',
            apptDate: dateFilter,
            apptCenter: centerFilter,
            childDob: b.child_dob || (b.child_age !== undefined ? `${new Date().getFullYear() - b.child_age}-01-01` : undefined),
            childGender: b.child_gender || b.gender,
            photoKey: b.child_profile
          };
        });

        const detailsList = await Promise.all(detailPromises);
        apptResults = detailsList.filter((d) => d !== null);
      }

      let finalData: any[] = [];
      if (isBasicSearch && isApptSearch) {
        finalData = apptResults.filter(ar => 
          basicResults.some(br => br.emrId === ar.emrId)
        );
      } else if (isApptSearch) {
        finalData = apptResults;
      } else {
        finalData = basicResults;
      }

      const centerMap: Record<string, string> = {};
      centers.forEach(c => { centerMap[c._id] = c.name });

      const mappedRecords: EmrRecord[] = finalData.map(item => {
        const screeningDate = item.screeningDate || item.screening_date || item.apptDate || item.date || '-';
        const screeningCenter = item.screeningCenter || item.screening_center || item.apptCenter || item.center || '-';

        return {
          id: item._id || item.emrId,
          emrId: item.emrId,
          parentName: item.parentName || '-',
          childName: item.childName || '-',
          date: formatDisplayDate(screeningDate),
          center: screeningCenter ? (centerMap[screeningCenter] || screeningCenter) : '-',
          childDob: item.childDob,
          childGender: item.childGender,
          photoKey: item.photoKey
        };
      });

      setRecords(mappedRecords);
    } catch (err) {
      console.error("Error searching:", err);
      setError("Failed to fetch records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRecord = async (record: EmrRecord) => {
    setSelectedRecordId(record.emrId);
    setActiveTab('Reports');
    setS3Files([]);
    setFilesLoading(true);

    setAnswersData([]);
    setAnswersLoading(true);
    setAnswersError(null);
    setOpenSubsections({});

    try {
      const detailRes = await api.parentchildApi.get(`/children/emr/${record.emrId}/details`);
      const detailPayload = detailRes?.data?.data ?? detailRes?.data ?? null;
      const detailData = Array.isArray(detailPayload) ? detailPayload[0] : detailPayload;

      if (detailData) {
        const screeningDate = detailData.screeningDate || detailData.screening_date || record.date;
        const screeningCenter = detailData.screeningCenter || detailData.screening_center || record.center;

        setSelectedRecordDetails({
          ...record,
          emrId: detailData.emrId || record.emrId,
          parentName: detailData.parentName || record.parentName || '-',
          childName: detailData.childName || record.childName || '-',
          childDob: detailData.childDob || record.childDob,
          childGender: detailData.childGender || record.childGender,
          date: formatDisplayDate(screeningDate || record.date),
          center: screeningCenter || record.center,
        });
      } else {
        setSelectedRecordDetails(record);
      }
    } catch (err) {
      console.error('Error fetching EMR details:', err);
      setSelectedRecordDetails(record);
    }
    
    // 1. Fetch S3 files
    try {
      const getRes = await api.dataCollectionApi.get(`/api/s3/emr/${record.emrId}`);
      if (getRes.data && getRes.data.ok && getRes.data.files) {
         const presignPromises = getRes.data.files.map(async (file: any) => {
             try {
                 const postRes = await api.dataCollectionApi.post('/api/s3/presign-download', {
                     key: file.key,
                     expiresIn: 3600
                 });
                 if (postRes.data && postRes.data.url) {
                     return { ...file, viewUrl: postRes.data.url };
                 }
             } catch (err) {
                 console.error("Failed to presign url for key:", file.key, err);
             }
             return null;
         });
         const resolvedFiles = (await Promise.all(presignPromises)).filter(img => img !== null);
         setS3Files(resolvedFiles);
      }
    } catch (err) {
      console.error("Error fetching S3 files:", err);
    } finally {
      setFilesLoading(false);
    }

    // 2. Fetch Questionnaire Full Answers (Historical, Health, Daily Diary)
    try {
      const answersRes = await api.Questionnair.get(`/api/answers/full/${record.emrId}`);
      if (answersRes.data && answersRes.data.data) {
        setAnswersData(answersRes.data.data);
      } else {
        setAnswersData([]);
      }
    } catch (err: any) {
      console.error("Error fetching questionnaire answers:", err);
      setAnswersError(err.message || "Failed to load questionnaire answers.");
      setAnswersData([]);
    } finally {
      setAnswersLoading(false);
    }
  };

  const formatDisplayDate = (value?: string) => {
    if (!value || value === '-' || value === 'null') return '-';

    const rawValue = String(value).trim();
    const dateOnly = rawValue.split('T')[0];

    if (dateOnly && dateOnly !== rawValue) {
      return dateOnly;
    }

    const parsedDate = new Date(rawValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return rawValue;
  };

  const calculateAge = (dobString?: string) => {
    if (!dobString) return 'Unknown age';
    const dob = new Date(dobString);
    const diff = Date.now() - dob.getTime();
    const ageDate = new Date(diff); 
    return Math.abs(ageDate.getUTCFullYear() - 1970) + " years";
  };

  const getIconForReport = (reportType: string = '', contentType: string = '', key: string = '') => {
    const lowerKey = key.toLowerCase();
    const lowerType = contentType.toLowerCase();
    if (lowerType.includes('audio') || lowerKey.endsWith('.ogg') || lowerKey.endsWith('.mp3') || lowerKey.endsWith('.wav')) {
      return <Music className="emr-file-icon" size={24} />;
    }
    if (lowerType.includes('image') || lowerKey.endsWith('.png') || lowerKey.endsWith('.jpg') || lowerKey.endsWith('.jpeg') || lowerKey.endsWith('.webp')) {
      return <ImageIcon className="emr-file-icon" size={24} />;
    }
    return <FileText className="emr-file-icon" size={24} />;
  };

  const getFileDisplayInfo = (file: any) => {
    const moduleName = file.moduleName || file.category || '';
    const key = file.key || '';
    const filename = file.originalFilename || key.split('/').pop() || '';
    const contentType = file.contentType || '';
    const lowerKey = key.toLowerCase();
    const lowerFile = filename.toLowerCase();

    const formattedModule = moduleName
      ? moduleName
          .split(/[-_]/)
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ')
      : '';

    let title = file.reportType || '';

    if (!title) {
      // Audio / Vitals auscultation
      if (lowerKey.includes('heart') || lowerFile.includes('heart')) {
        title = 'Heart Auscultation';
      } else if (lowerKey.includes('lung') || lowerFile.includes('lung')) {
        title = 'Lung Auscultation';
      } else if (lowerKey.includes('arteries') || lowerFile.includes('arteries') || lowerFile.includes('artery')) {
        title = 'Arteries Auscultation';
      } else if (lowerKey.includes('abdomen') || lowerFile.includes('abdomen')) {
        title = 'Abdomen Auscultation';
      } else if (lowerKey.includes('auscultation') || lowerFile.includes('auscultation')) {
        title = 'Auscultation Sound';
      }
      // Oral imaging
      else if (lowerKey.includes('anterior') || lowerFile.includes('anterior')) {
        title = 'Anterior View';
      } else if (lowerKey.includes('right-buccal') || lowerFile.includes('right-buccal') || lowerFile.includes('right_buccal')) {
        title = 'Right Buccal View';
      } else if (lowerKey.includes('left-buccal') || lowerFile.includes('left-buccal') || lowerFile.includes('left_buccal')) {
        title = 'Left Buccal View';
      } else if (lowerKey.includes('maxillary') || lowerFile.includes('maxillary')) {
        title = 'Maxillary View';
      } else if (lowerKey.includes('mandibular') || lowerFile.includes('mandibular')) {
        title = 'Mandibular View';
      } else if (lowerKey.includes('mallampati') || lowerFile.includes('mallampati')) {
        title = 'Mallampati View';
      }
      // Reports
      else if (lowerKey.includes('master') || lowerFile.includes('master') || moduleName === 'qavach-reports') {
        title = 'Master Screening Report';
      } else if (lowerKey.includes('growth-chart') || lowerFile.includes('growth') || moduleName === 'growth-chart') {
        title = 'Growth Chart Report';
      } else if (lowerKey.includes('vision') || lowerFile.includes('vision') || moduleName === 'vision-screening') {
        title = 'Vision Screening Report';
      } else if (formattedModule) {
        title = `${formattedModule} ${contentType.includes('pdf') ? 'Report' : contentType.includes('audio') ? 'Audio' : contentType.includes('image') ? 'Image' : 'File'}`;
      } else {
        title = filename || 'Document';
      }
    }

    return {
      title,
      moduleName,
      formattedModule,
      filename,
    };
  };

  const getFilteredReports = () => {
    if (activeTab === 'Reports') {
      return s3Files.filter(f => 
        f.contentType?.includes('pdf') || 
        f.key?.toLowerCase().endsWith('.pdf') ||
        f.moduleName?.includes('report') ||
        f.moduleName?.includes('chart') ||
        (f.moduleName?.includes('screening') && !f.contentType?.includes('image') && !f.contentType?.includes('audio'))
      );
    }
    if (activeTab === 'Audio') {
      return s3Files.filter(f => 
        f.contentType?.includes('audio') || 
        f.contentType?.includes('video') || 
        f.key?.toLowerCase().endsWith('.mp4') || 
        f.key?.toLowerCase().endsWith('.mp3') || 
        f.key?.toLowerCase().endsWith('.wav') ||
        f.key?.toLowerCase().endsWith('.ogg') ||
        (f.moduleName === 'vitals' && (f.key?.includes('auscultation') || f.contentType?.includes('audio') || f.key?.endsWith('.ogg')))
      );
    }
    if (activeTab === 'Images') {
      return s3Files.filter(f => 
        f.contentType?.includes('image') || 
        f.key?.toLowerCase().endsWith('.png') || 
        f.key?.toLowerCase().endsWith('.jpeg') || 
        f.key?.toLowerCase().endsWith('.jpg') ||
        f.key?.toLowerCase().endsWith('.webp')
      );
    }
    return s3Files;
  };

  const groupQuestionsBySubsection = (questions: any[]) => {
    if (!Array.isArray(questions) || questions.length === 0) return [];

    const groupsMap = new Map<string, { key: string; title: string; intro?: string; questions: any[] }>();

    questions.forEach((qItem: any) => {
      let rawTitle = qItem.questionDetails?.subsectionTitle?.trim();
      if (!rawTitle) {
        const qId = qItem.questionId || '';
        const prefix = qId.split('_')[0];
        rawTitle = prefix ? `${prefix} Section` : 'General Questions';
      }

      const cleanTitle = rawTitle.replace(/\$/g, ',').trim();
      const groupKey = cleanTitle.toLowerCase();

      if (!groupsMap.has(groupKey)) {
        const rawIntro = qItem.questionDetails?.subsectionIntro || '';
        const cleanIntro = rawIntro ? rawIntro.replace(/\$/g, ',').trim() : '';

        groupsMap.set(groupKey, {
          key: groupKey,
          title: cleanTitle,
          intro: cleanIntro,
          questions: []
        });
      }

      groupsMap.get(groupKey)!.questions.push(qItem);
    });

    return Array.from(groupsMap.values());
  };

  const toggleSubsection = (key: string) => {
    setOpenSubsections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExpandAllSubsections = (keys: string[]) => {
    setOpenSubsections(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = true; });
      return next;
    });
  };

  const handleCollapseAllSubsections = (keys: string[]) => {
    setOpenSubsections(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = false; });
      return next;
    });
  };

  const [activeTab, setActiveTab] = useState<string>('All Files');

  return (
    <div className="emr-management-container">
      <h2 className="emr-page-title">Electronic Medical Record (EMR)</h2>

      {/* Filters Section */}
      <div className="emr-filters">
        <div className="emr-filter-group">
          <label>EMR ID</label>
          <input 
            type="text" 
            className="emr-input" 
            placeholder="Enter EMR ID" 
            value={emrIdFilter}
            onChange={(e) => setEmrIdFilter(e.target.value)}
          />
        </div>
        <div className="emr-filter-group">
          <label>Parent Name</label>
          <input 
            type="text" 
            className="emr-input" 
            placeholder="Enter parent name" 
            value={parentNameFilter}
            onChange={(e) => setParentNameFilter(e.target.value)}
          />
        </div>
        <div className="emr-filter-group">
          <label>Child Name</label>
          <input 
            type="text" 
            className="emr-input" 
            placeholder="Enter child name" 
            value={childNameFilter}
            onChange={(e) => setChildNameFilter(e.target.value)}
          />
        </div>
        <div className="emr-filter-group">
          <label>Screening Date</label>
          <input 
            type="date" 
            className="emr-input" 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
        <div className="emr-filter-group">
          <label>Screening Center</label>
          <select 
            className="emr-select"
            value={centerFilter}
            onChange={(e) => setCenterFilter(e.target.value)}
          >
            <option value="">Select center</option>
            {centers.map(center => (
              <option key={center._id} value={center._id}>{center.name}</option>
            ))}
          </select>
        </div>
        <button className="btn-search" onClick={handleSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search Records'}
        </button>
      </div>

      {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}

      {/* Table Section */}
      <div className="emr-table-container">
        <table className="emr-table">
          <thead>
            <tr>
              <th>EMR ID</th>
              <th>Parent Name</th>
              <th>Child Name</th>
              <th>Date</th>
              <th>Center</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td>{record.emrId}</td>
                <td>{record.parentName}</td>
                <td>{record.childName}</td>
                <td>{record.date}</td>
                <td>{record.center}</td>
                <td>
                  <button 
                    className="btn-open"
                    onClick={() => handleOpenRecord(record)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
            {!loading && records.length === 0 && !error && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: '#64748b', fontSize: '15px' }}>
                  No records found matching your search criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Details Section */}
      {selectedRecordId && selectedRecordDetails && (
        <div className="emr-details-card">
          <div className="emr-details-header">
            <h3 className="emr-details-title">Patient EMR Details</h3>
          </div>
          <div className="emr-details-body">
            <div className="emr-info-grid">
              <div className="emr-info-item">
                <span className="emr-info-label">Patient Name</span>
                <span className="emr-info-value">{selectedRecordDetails.childName}</span>
              </div>
              <div className="emr-info-item">
                <span className="emr-info-label">Parent Name</span>
                <span className="emr-info-value">{selectedRecordDetails.parentName}</span>
              </div>
              <div className="emr-info-item">
                <span className="emr-info-label">Age / Gender</span>
                <span className="emr-info-value">
                  {calculateAge(selectedRecordDetails.childDob)} / {selectedRecordDetails.childGender || 'Unknown'}
                </span>
              </div>
              <div className="emr-info-item">
                <span className="emr-info-label">EMR ID</span>
                <span className="emr-info-value">{selectedRecordDetails.emrId}</span>
              </div>
              <div className="emr-info-item">
                <span className="emr-info-label">Screening Center</span>
                <span className="emr-info-value">{selectedRecordDetails.center}</span>
              </div>
              <div className="emr-info-item">
                <span className="emr-info-label">Screening Date</span>
                <span className="emr-info-value">{selectedRecordDetails.date}</span>
              </div>
            </div>

            <div className="emr-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`emr-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="emr-tab-content-scroll">
              {['History', 'Questionnaire', 'Dairy'].includes(activeTab) ? (
              <div className="emr-qa-section">
                {answersLoading ? (
                  <div style={{ color: '#666', padding: '30px', textAlign: 'center' }}>
                    Loading questionnaire responses...
                  </div>
                ) : answersError ? (
                  <div style={{ color: '#dc2626', padding: '30px', textAlign: 'center' }}>
                    {answersError}
                  </div>
                ) : (() => {
                    let targetSlugs: string[] = [];
                    if (activeTab === 'History') {
                      targetSlugs = ['historical', 'historical_questions', 'historical-questions'];
                    } else if (activeTab === 'Questionnaire') {
                      targetSlugs = ['health', 'health_questions', 'health-questions'];
                    } else if (activeTab === 'Dairy') {
                      targetSlugs = ['daily_dairy', 'daily_diary', 'daily-dairy', 'daily-diary', 'daily_dairy_questions', 'daily_diary_questions'];
                    }

                    const categoryBlocks = (answersData || []).filter((item: any) =>
                      targetSlugs.includes(String(item.categorySlug || '').toLowerCase())
                    );

                    if (categoryBlocks.length === 0) {
                      return (
                        <div style={{ color: '#64748b', padding: '30px', textAlign: 'center', fontSize: '15px' }}>
                          No {activeTab} answers submitted for this child.
                        </div>
                      );
                    }

                    return categoryBlocks.map((catBlock: any, bIdx: number) => {
                      const groups = groupQuestionsBySubsection(catBlock.data || []);
                      const allGroupKeys = groups.map(g => `${catBlock._id || bIdx}_${g.key}`);
                      const totalQuestions = (catBlock.data || []).length;
                      const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every((k) => !!openSubsections[k]);

                      return (
                        <div key={catBlock._id || bIdx} className="emr-qa-card" style={{ marginBottom: '24px' }}>
                          <div className="emr-qa-card-header">
                            <div className="emr-qa-card-header-left">
                              <span className="qa-version-badge">Version {catBlock.version || 1}</span>
                              <span className="emr-qa-total-badge">{groups.length} Sections • {totalQuestions} Questions</span>
                              {catBlock.createdAt && (
                                <span className="qa-date">Submitted: {new Date(catBlock.createdAt).toLocaleString('en-IN')}</span>
                              )}
                            </div>
                            <div className="emr-qa-card-header-right">
                              <button
                                type="button"
                                className="btn-toggle-all"
                                onClick={() => allExpanded ? handleCollapseAllSubsections(allGroupKeys) : handleExpandAllSubsections(allGroupKeys)}
                              >
                                {allExpanded ? 'Collapse All' : 'Expand All'}
                              </button>
                            </div>
                          </div>

                          <div className="emr-qa-groups-container">
                            {groups.map((group) => {
                              const groupKey = `${catBlock._id || bIdx}_${group.key}`;
                              const isOpen = !!openSubsections[groupKey];
                              const answeredCount = group.questions.filter((q: any) => {
                                const v = q.value;
                                if (v === null || v === undefined || v === '') return false;
                                if (Array.isArray(v) && v.length === 0) return false;
                                return true;
                              }).length;

                              return (
                                <div key={group.key} className={`emr-accordion-item ${isOpen ? 'open' : ''}`}>
                                  <button
                                    type="button"
                                    className="emr-accordion-header"
                                    onClick={() => toggleSubsection(groupKey)}
                                  >
                                    <div className="emr-accordion-title-group">
                                      <ChevronRight size={18} className="emr-accordion-chevron" />
                                      <span className="emr-accordion-title">{group.title}</span>
                                      <span className="emr-subsection-count">{group.questions.length} Questions</span>
                                    </div>
                                    <div className="emr-accordion-meta-group">
                                      <span className="emr-answered-badge">
                                        Answered: {answeredCount}/{group.questions.length}
                                      </span>
                                    </div>
                                  </button>

                                  {isOpen && (
                                    <div className="emr-accordion-body">
                                      {group.intro && (
                                        <div className="emr-subsection-intro">
                                          <Info size={16} className="emr-intro-icon" />
                                          <span>{group.intro}</span>
                                        </div>
                                      )}
                                      <table className="emr-qa-table">
                                        <thead>
                                          <tr>
                                            <th style={{ width: '130px' }}>Q. ID</th>
                                            <th>Question / Parameter</th>
                                            <th style={{ width: '40%' }}>Submitted Answer</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {group.questions.map((qItem: any, qIdx: number) => {
                                            const qText =
                                              qItem.questionDetails?.text ||
                                              qItem.questionDetails?.questionText ||
                                              qItem.questionDetails?.title ||
                                              qItem.questionDetails?.label ||
                                              qItem.questionText ||
                                              qItem.questionId;

                                            const val = qItem.value;
                                            const answerType = qItem.questionDetails?.answerType;

                                            return (
                                              <tr key={qItem.questionId || qIdx}>
                                                <td>
                                                  <span className="emr-qa-qid">{qItem.questionId}</span>
                                                </td>
                                                <td>
                                                  <div className="emr-qa-question-wrapper">
                                                    <span className="emr-qa-question-text">{qText}</span>
                                                    {answerType && (
                                                      <span className="emr-qa-type-badge">{answerType}</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  {Array.isArray(val) ? (
                                                    <div className="emr-qa-tags">
                                                      {val.map((v: any, i: number) => (
                                                        <span key={i} className="emr-qa-badge">
                                                          <CheckCircle2 size={12} className="emr-badge-check" />
                                                          {String(v)}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  ) : typeof val === 'object' && val !== null ? (
                                                    <div className="emr-qa-obj-card">
                                                      {Object.entries(val).map(([k, v]) => (
                                                        <div key={k} className="emr-qa-obj-row">
                                                          <span className="emr-qa-obj-key">{k}:</span>
                                                          <span className="emr-qa-obj-val">{String(v)}</span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : val === '' || val === null || val === undefined ? (
                                                    <span className="emr-qa-empty">— (Not answered)</span>
                                                  ) : (
                                                    <span className="emr-qa-answer">{String(val)}</span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()
                }
              </div>
            ) : filesLoading ? (
              <div style={{ color: '#666', padding: '10px' }}>Loading files from S3...</div>
            ) : activeTab === 'Images' ? (
              <div className="emr-images-grid">
                {getFilteredReports().length > 0 ? (
                  getFilteredReports().map((file, idx) => {
                    const isImage = file.contentType && file.contentType.startsWith('image/');
                    const info = getFileDisplayInfo(file);
                    return (
                      <div className="emr-image-card" key={file._id || idx}>
                        {isImage ? (
                          <div className="emr-image-preview-container">
                            <img src={file.viewUrl} alt={info.title} className="emr-image-preview" />
                          </div>
                        ) : (
                          <div className="emr-file-preview-placeholder">
                            <FileText size={48} color="#94a3b8" />
                          </div>
                        )}
                        <div className="emr-file-card-footer">
                          <div className="emr-file-info">
                            <ImageIcon className="emr-file-icon" size={24} />
                            <div className="emr-file-details">
                              <span className="emr-file-name" title={info.filename}>{info.title}</span>
                              {info.moduleName && (
                                <span className="emr-file-meta">
                                  <span className="emr-module-badge">Module: {info.moduleName}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <a href={file.viewUrl} target="_blank" rel="noreferrer" className="emr-file-download" title="View/Download">
                            <ExternalLink size={20} />
                          </a>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: '#666', padding: '10px' }}>No images found for this patient.</div>
                )}
              </div>
            ) : (
              <div className="emr-files-grid">
                {getFilteredReports().length > 0 ? (
                  getFilteredReports().map((report, idx) => {
                    const info = getFileDisplayInfo(report);
                    const isAudio = report.contentType?.includes('audio') || report.key?.endsWith('.ogg') || report.key?.endsWith('.mp3') || report.key?.endsWith('.wav');
                    return (
                      <div className="emr-file-card" key={report._id || idx}>
                        <div className="emr-file-card-main">
                          <div className="emr-file-info">
                            {getIconForReport(report.reportType, report.contentType, report.key)}
                            <div className="emr-file-details">
                              <span className="emr-file-name" title={info.filename}>{info.title}</span>
                              {info.moduleName && (
                                <span className="emr-file-meta">
                                  <span className="emr-module-badge">Module: {info.moduleName}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <a href={report.viewUrl} target="_blank" rel="noreferrer" className="emr-file-download" title="Download">
                            <Download size={20} />
                          </a>
                        </div>
                        {report.viewUrl && isAudio && (
                          <div className="emr-audio-player-wrapper">
                            <audio controls src={report.viewUrl} className="emr-inline-audio" preload="none" />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: '#666', padding: '10px' }}>No {activeTab.toLowerCase()} found for this patient.</div>
                )}
              </div>
            )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmrManagement;
